import { useRef, useState } from "react";
import api, { formatApiError, resolveUrl } from "@/lib/api";
import { Upload, Film, X, Loader2 } from "lucide-react";

/**
 * Drag-and-drop video uploader.
 * Calls POST /api/admin/uploads/file and returns the absolute URL via onChange.
 *
 * Props:
 *  - value: current video URL (absolute, "/api/uploads/..." or external)
 *  - onChange(url): called with the new URL after upload, or "" after clear
 *  - testId: prefix for data-testid
 *  - hint: optional helper text rendered below the dropzone
 */
export default function VideoUploader({ value, onChange, testId = "video-uploader", hint }) {
    const inputRef = useRef(null);
    const [uploading, setUploading] = useState(false);
    const [progress, setProgress] = useState(0);
    const [error, setError] = useState("");
    const [dragOver, setDragOver] = useState(false);

    const previewSrc = !value ? null : (value.startsWith("http") ? value : resolveUrl(value));

    const doUpload = async (file) => {
        setError("");
        if (!file) return;
        if (!file.type?.startsWith("video/") && !/\.(mp4|webm)$/i.test(file.name || "")) {
            setError("Selecione um arquivo de vídeo (.mp4 ou .webm).");
            return;
        }
        if (file.size > 50 * 1024 * 1024) {
            setError("Arquivo muito grande. Máximo: 50 MB. Comprima o vídeo antes de enviar.");
            return;
        }
        setUploading(true);
        setProgress(0);
        try {
            const form = new FormData();
            form.append("file", file);
            const { data } = await api.post("/admin/uploads/file", form, {
                headers: { "Content-Type": "multipart/form-data" },
                timeout: 5 * 60 * 1000,
                onUploadProgress: (e) => {
                    if (e.total) setProgress(Math.round((e.loaded / e.total) * 100));
                },
            });
            onChange(data.url);
        } catch (e) {
            setError(formatApiError(e));
        } finally {
            setUploading(false);
            setProgress(0);
        }
    };

    const onDrop = (e) => {
        e.preventDefault();
        setDragOver(false);
        const file = e.dataTransfer.files?.[0];
        doUpload(file);
    };

    const clear = () => onChange("");

    return (
        <div data-testid={testId} className="space-y-3">
            {previewSrc ? (
                <div className="relative aspect-video w-full rounded-2xl overflow-hidden bg-[#0A0A0A] border border-black/10">
                    <video
                        key={previewSrc}
                        src={previewSrc}
                        muted
                        loop
                        playsInline
                        autoPlay
                        className="w-full h-full object-cover"
                        data-testid={`${testId}-preview`}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                    <div className="absolute bottom-3 left-3 right-3 flex justify-between gap-2">
                        <button
                            type="button"
                            onClick={() => inputRef.current?.click()}
                            disabled={uploading}
                            data-testid={`${testId}-replace`}
                            className="btn-ghost !bg-white/90 !py-2 !px-3 text-xs"
                        >
                            {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                            {uploading ? `Enviando… ${progress}%` : "Trocar vídeo"}
                        </button>
                        <button
                            type="button"
                            onClick={clear}
                            data-testid={`${testId}-clear`}
                            className="btn-ghost !bg-white/90 !py-2 !px-3 text-xs !text-red-600 !border-red-200"
                        >
                            <X className="w-3.5 h-3.5" /> Remover
                        </button>
                    </div>
                </div>
            ) : (
                <button
                    type="button"
                    onClick={() => inputRef.current?.click()}
                    onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                    onDragLeave={() => setDragOver(false)}
                    onDrop={onDrop}
                    data-testid={`${testId}-dropzone`}
                    className={`w-full aspect-video max-h-[280px] rounded-2xl border-2 border-dashed flex flex-col items-center justify-center gap-3 transition-all
                        ${dragOver ? "border-[#00D4D4] bg-[#00D4D4]/5" : "border-black/15 bg-[#FAFAF7] hover:border-black/30 hover:bg-white"}`}
                >
                    {uploading ? (
                        <>
                            <Loader2 className="w-7 h-7 text-[#5C5C5C] animate-spin" />
                            <div className="font-display text-sm text-[#5C5C5C]">Enviando vídeo… {progress}%</div>
                        </>
                    ) : (
                        <>
                            <div className="w-12 h-12 rounded-full bg-white border border-black/5 flex items-center justify-center">
                                <Film className="w-5 h-5 text-[#5C5C5C]" />
                            </div>
                            <div className="text-center px-4">
                                <div className="font-display text-sm">Clique ou arraste o vídeo aqui</div>
                                <div className="font-body text-xs text-[#5C5C5C] mt-1">MP4 ou WebM · até 50 MB · loop automático sem som</div>
                            </div>
                        </>
                    )}
                </button>
            )}

            <input
                ref={inputRef}
                type="file"
                accept="video/mp4,video/webm,.mp4,.webm"
                className="hidden"
                data-testid={`${testId}-input`}
                onChange={(e) => doUpload(e.target.files?.[0])}
            />

            {hint && <div className="font-body text-xs text-[#5C5C5C]">{hint}</div>}

            {error && (
                <div data-testid={`${testId}-error`} className="text-xs text-red-600 bg-red-50 border border-red-100 px-3 py-2 rounded-lg">
                    {error}
                </div>
            )}
        </div>
    );
}
